import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  CheckoutExtensionUI,
  CheckoutProductUpgradeExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, pairwise, startWith, takeUntil } from 'rxjs/operators';
import { ViewMode } from '../../../models';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { generateRandomString } from '@utils/string-utils';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { type IDynamicFieldsOptions } from '@pdqdev/pdq-shared/dist/interfaces/IDynamicFields';
import {
  AMOUNT_LEFT_TOKEN,
  DELIVERY_FREQUENCY_TOKEN,
  DISCOUNT_DELTA_TOKEN,
  PRODUCT_NAME_TOKEN,
} from './dynamic-fields';
import { customDynamicFieldValidator } from '../../shared/validators/dynamic-fields.validator';
import {
  createRuleGroupFormGroup,
  DEFAULT_BADGE,
  DEFAULT_UPGRADE_RULE_GROUP,
  isFocusedOfferLayout,
  LayoutSnapshot,
  ProductSelectionFormGroup,
  TypedRuleGroup,
  UpgradeFormGroup,
} from './models';

enum ProductUpgradeTab {
  SETTINGS = 'settings',
  APPEARANCE = 'appearance',
  PRODUCT_SELECTION = 'product_selection',
  DISPLAY_CONDITIONS = 'display_conditions',
}

@Component({
  selector: 'product-upgrade',
  templateUrl: './product-upgrade.component.html',
  styleUrls: ['./product-upgrade.component.scss'],
  standalone: false,
})
export class ProductUpgradeComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;

  tabs = [
    { label: 'Settings', value: ProductUpgradeTab.SETTINGS, disabled: false },
    { label: 'Appearance', value: ProductUpgradeTab.APPEARANCE, disabled: false },
    { label: 'Product Selection', value: ProductUpgradeTab.PRODUCT_SELECTION, disabled: false },
    { label: 'Display Conditions', value: ProductUpgradeTab.DISPLAY_CONDITIONS, disabled: false },
  ];
  ProductUpgradeTab = ProductUpgradeTab;
  activeTab = ProductUpgradeTab.SETTINGS;
  formGroup: FormGroup<UpgradeFormGroup>;
  layoutSwitchSnapshot: LayoutSnapshot | null = null;
  entities;
  readonly formArrayFieldName = 'restrictionsRules';
  restrictionsRules = getExtensionRules();
  dynamicFieldsAutocompleteOptions$ = new BehaviorSubject<IDynamicFieldsOptions[]>([
    AMOUNT_LEFT_TOKEN,
    PRODUCT_NAME_TOKEN,
    DISCOUNT_DELTA_TOKEN,
    DELIVERY_FREQUENCY_TOKEN,
  ]);

  constructor(
    private store: Store<CheckoutReducer.State>,
    private actions$: Actions,
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
  ) {}

  ngOnInit() {
    this.entities = this.restrictionsRules.map((r) => r.entity);
    this.initForm();
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.PRODUCT_UPGRADE);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });
    this.actions$
      .pipe(
        ofType(CheckoutActions.updateExtensionSuccess, CheckoutActions.saveExtensionSuccess),
        takeUntil(this.destroy$),
      )
      .subscribe(() => (this.layoutSwitchSnapshot = null));

    this.extensionsService.initDynamicFieldsAutocomplete();
    this.extensionsService.dynamicFieldsAutocomplete$.pipe(takeUntil(this.destroy$)).subscribe((dynamicFields) => {
      if (dynamicFields) {
        const filtered = dynamicFields.filter(
          (field) => field.name !== '{countdown}' && field.name !== '{shipping_method.pdq_iq}',
        );
        this.dynamicFieldsAutocompleteOptions$.next([
          AMOUNT_LEFT_TOKEN,
          PRODUCT_NAME_TOKEN,
          DISCOUNT_DELTA_TOKEN,
          DELIVERY_FREQUENCY_TOKEN,
          ...filtered,
        ]);
        this.revalidateDynamicFieldControls();
      }
    });

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension.template, extension.usageToken);
      }
    });

    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  onTabChange(event: any) {
    this.activeTab = event;
  }

  switchToFocusedOffer(): void {
    this.formGroup.controls.settings.patchValue({
      displayMode: CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK,
      layoutStyle: 'focused',
      allowRevert: false,
    });
  }

  undoLayoutSwitch(): void {
    if (this.layoutSwitchSnapshot) {
      this.formGroup.controls.settings.patchValue(this.layoutSwitchSnapshot);
    }
  }

  private buildBadgeFormGroup(loaded?: Partial<typeof DEFAULT_BADGE>) {
    return new FormGroup({
      enabled: new FormControl(loaded?.enabled ?? DEFAULT_BADGE.enabled),
      text: new FormControl(loaded?.text ?? DEFAULT_BADGE.text, this.dynamicFieldValidator),
      backgroundColor: new FormControl(loaded?.backgroundColor ?? DEFAULT_BADGE.backgroundColor),
      textColor: new FormControl(loaded?.textColor ?? DEFAULT_BADGE.textColor),
      cornerRadius: new FormControl(loaded?.cornerRadius ?? DEFAULT_BADGE.cornerRadius),
      icon: new FormControl<string | null>(loaded?.icon ?? null),
    });
  }

  private get dynamicFieldValidator() {
    return customDynamicFieldValidator(() => this.dynamicFieldsAutocompleteOptions$.getValue());
  }

  private revalidateDynamicFieldControls() {
    const appearance = this.formGroup?.get('appearance');
    if (!appearance) return;
    const paths = [
      'titleText.label',
      'titleText.badge.text',
      'upgradeText.label',
      'upgradeText.badge.text',
      'button.label',
      'button.labelForProductsWithVariants',
    ];
    paths.forEach((path) => appearance.get(path)?.updateValueAndValidity({ emitEvent: false }));
  }

  dispatchExampleViewValues(values: FormGroup<UpgradeFormGroup>['value']) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.PRODUCT_UPGRADE]: {
        mode,
        template: values,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: CheckoutProductUpgradeExtension.Template, usageToken?: string) {
    this.formGroup = new FormGroup<UpgradeFormGroup>({
      usageToken: new FormControl<string>(usageToken ?? 'product_upgrade_' + generateRandomString(12)),
      internalName: new FormControl<string>(loadedValues?.internalName ?? 'Product Upgrade', Validators.required),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.restrictionsRules ?? [],
        getExtensionRules(),
      ) as FormArray<FormGroup<TypedRuleGroup>>,

      settings: new FormGroup({
        displayMode: new FormControl(
          loadedValues?.settings?.displayMode ?? CheckoutProductUpgradeExtension.DisplayMode.LINE_ITEM_ACTION,
        ),
        variantSelector: new FormControl(loadedValues?.settings?.variantSelector ?? 'inside'),
        allowRevert: new FormControl(loadedValues?.settings?.allowRevert ?? false),
        revertButtonLabel: new FormControl(loadedValues?.settings?.revertButtonLabel ?? 'Revert'),
        allowSubscriptions: new FormControl(loadedValues?.settings?.allowSubscriptions ?? false),
        subscriptionFrequency: new FormControl(loadedValues?.settings?.subscriptionFrequency ?? 'strict'),
        noMatchingPlanBehavior: new FormControl(loadedValues?.settings?.noMatchingPlanBehavior ?? 'useFirstAvailable'),
        layoutStyle: new FormControl(loadedValues?.settings?.layoutStyle ?? 'comparison'),
      }),

      appearance: new FormGroup({
        titleText: new FormGroup({
          label: new FormControl(
            loadedValues?.appearance?.titleText?.label ?? 'Upgrade Available',
            this.dynamicFieldValidator,
          ),
          appearance: new FormControl(
            loadedValues?.appearance?.titleText?.appearance ?? CheckoutExtensionUI.Appearance.BASE,
          ),
          badge: this.buildBadgeFormGroup(loadedValues?.appearance?.titleText?.badge),
        }),
        upgradeText: new FormGroup({
          label: new FormControl(loadedValues?.appearance?.upgradeText?.label ?? 'Upgrade', this.dynamicFieldValidator),
          appearance: new FormControl(
            loadedValues?.appearance?.upgradeText?.appearance ?? CheckoutExtensionUI.Appearance.ACCENT,
          ),
          badge: this.buildBadgeFormGroup(loadedValues?.appearance?.upgradeText?.badge),
        }),
        button: new FormGroup({
          label: new FormControl(loadedValues?.appearance?.button?.label ?? 'Upgrade', this.dynamicFieldValidator),
          labelForProductsWithVariants: new FormControl(
            loadedValues?.appearance?.button?.labelForProductsWithVariants ?? 'Select an option to upgrade',
            this.dynamicFieldValidator,
          ),
          appearance: new FormControl(
            loadedValues?.appearance?.button?.appearance ?? CheckoutExtensionUI.Appearance.INTERACTIVE,
          ),
        }),
        mainBackground: new FormGroup({
          border: new FormControl(
            loadedValues?.appearance?.mainBackground?.border ?? CheckoutExtensionUI.BorderType.NONE,
          ),
          borderWidth: new FormControl(
            loadedValues?.appearance?.mainBackground?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE,
          ),
          cornerRadius: new FormControl<CheckoutExtensionUI.CornerRadius>(
            loadedValues?.appearance?.mainBackground?.cornerRadius === CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED
              ? CheckoutExtensionUI.CornerRadius.LARGE
              : (loadedValues?.appearance?.mainBackground?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE),
          ),
          padding: new FormControl(
            loadedValues?.appearance?.mainBackground?.padding ?? CheckoutExtensionUI.Padding.BASE,
          ),
        }),
        image: new FormGroup({
          border: new FormControl(loadedValues?.appearance?.image?.border ?? CheckoutExtensionUI.BorderType.NONE),
          borderWidth: new FormControl(
            loadedValues?.appearance?.image?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE,
          ),
          cornerRadius: new FormControl(
            loadedValues?.appearance?.image?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE,
          ),
          padding: new FormControl(loadedValues?.appearance?.image?.padding ?? CheckoutExtensionUI.Padding.BASE),
        }),
        completionBanner: new FormGroup({
          enabled: new FormControl(loadedValues?.appearance?.completionBanner?.enabled ?? false),
          text: new FormControl(loadedValues?.appearance?.completionBanner?.text ?? '', this.dynamicFieldValidator),
          tone: new FormControl(loadedValues?.appearance?.completionBanner?.tone ?? 'success'),
        }),
      }),

      productSelection: new FormArray<FormGroup<ProductSelectionFormGroup>>(
        (loadedValues?.productSelection?.length > 0
          ? loadedValues.productSelection
          : [{ ...DEFAULT_UPGRADE_RULE_GROUP, id: 0 }]
        ).map((ruleGroup, index: number) => {
          return createRuleGroupFormGroup(index, ruleGroup, this.rulesDynamicFormService);
        }),
      ),
    });

    this.dispatchExampleViewValues(this.formGroup.value);

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });

    this.layoutSwitchSnapshot = null;
    this.trackLayoutSwitch();
  }

  private trackLayoutSwitch(): void {
    const settings = this.formGroup.controls.settings;
    settings.valueChanges
      .pipe(
        map(() => settings.getRawValue()),
        startWith(settings.getRawValue()),
        pairwise(),
        takeUntil(this.destroy$),
      )
      .subscribe(([previous, current]) => {
        const wasFocused = isFocusedOfferLayout(previous);
        const isFocused = isFocusedOfferLayout(current);
        if (!wasFocused && isFocused) {
          const { displayMode, layoutStyle, allowRevert } = previous;
          this.layoutSwitchSnapshot = { displayMode, layoutStyle, allowRevert };
        } else if (wasFocused && !isFocused) {
          this.layoutSwitchSnapshot = null;
          this.resetSubscriptionUpgrades();
        }
      });
  }

  private resetSubscriptionUpgrades(): void {
    this.formGroup.controls.productSelection.controls.forEach((group) => {
      const { upgradeType, groupByKeyEnabled, groupByKey } = group.controls.upgradeProducts.controls;
      if (upgradeType.value === 'subscription') {
        upgradeType.setValue('product');
        groupByKeyEnabled.setValue(false);
        groupByKey.removeValidators(Validators.required);
        groupByKey.setValue('');
        groupByKey.updateValueAndValidity();
      }
    });
  }

  handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = JSON.parse(JSON.stringify(this.formGroup.value));
    const { usageToken, ...template } = formValue;

    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.PRODUCT_UPGRADE,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
  // Required stub: @onDestroyComplete() decorator only wraps ngOnDestroy if it already exists on the prototype.
  ngOnDestroy() {}
}
